import { useState } from 'react';
import registrationStatusMapper from '../helpers/registrationStatusMapper';
import { getRegistrationStatusClass } from '../helpers/getRegistrationStatusClass';
import PlayerPortalsForm from '../../../../../../common-forms-ui/src/lib/PlayerPortals/PlayerPortalsForm/PlayerPortalsForm';
import PolicyItem from './PolicyItem';
import { useCommonUIActionsContext } from '../../../../contexts/CommonUIActionsContext';
import {
  AUTH_TOKEN_KEY,
  UPDATE_MUTATION_KEY,
} from '../../../../constants/playerPortals';
import { getPPAuthToken } from 'libs/utils/src/lib/player-portals/pp-handle-token';
import { getPPUrl } from 'libs/utils/src/lib/player-portals/pp-api-utils';
import { ensureValidUri } from 'libs/utils/src/lib/url-utils';
import { Button } from '@mui/material';
import { Elements } from '@kontent-ai/delivery-sdk/lib/elements';
import RichText from '../../../RichText/RichText';
import { notifications } from '../../../ToastNotification/ToastNotification';
import {
  DEFAULT_ERROR_EMAIL_MESSAGE,
  DEFAULT_ERROR_SMS_MESSAGE,
  DEFAULT_SUCCESS_EMAIL_MESSAGE,
  DEFAULT_SUCCESS_SMS_MESSAGE,
  EMAIL_MUTATION_KEY,
  EmailConfig,
  getEmailModel,
  getSmsModel,
  SMS_MUTATION_KEY,
  SmsConfig,
} from '../helpers/communicationHelpers';
import {
  checkIfShowAcceptPoliciesBtn,
  checkIfShowCompleteRegistrationBtn,
} from '../helpers/policiesHelpers';
import { nanoid } from 'nanoid';

import styles from '../PlayerPortalsOnSiteRegistrationAdminPanel.module.scss';
import { RegistrationStatus } from '@r-and-a-shared-ui/utils';
import { getBearerToken } from '@r-and-a-shared-ui/auth-client';

type RegistrationFormProps = {
  participantId: string;
  registrationForm: any;
  championshipPolicies: {
    policySectionTitle: string;
    antiDopingPolicyText: string;
    antiBettingPolicyText: string;
    privacyPolicySectionText?: Elements.RichTextElement | undefined;
  };
  actionBtnLabels: {
    reSendSMSBtnLabel: string;
    reSendEmailBtnLabel: string;
    agreeToPoliciesBtnLabel: string;
    completeRegistrationBtnLabel: string;
  };
  isPlayersDataSource: boolean;
  communicationConfigs?: {
    smsConfig?: SmsConfig;
    emailConfig?: EmailConfig;
  };
};

export const RegistrationForm = (props: RegistrationFormProps) => {
  const {
    participantId,
    registrationForm,
    championshipPolicies,
    actionBtnLabels,
    isPlayersDataSource,
    communicationConfigs,
  } = props;

  const participantType = isPlayersDataSource
    ? 'championshipPlayer'
    : 'championshipCaddie';
  const [selectedParticipant, setSelectedParticipant] = useState<any>(null);
  const [formKey, setFormKey] = useState<string>('');

  const { smsConfig, emailConfig } = communicationConfigs || {};

  const callbackUrl = isPlayersDataSource
    ? process.env.NEXT_PUBLIC_UPDATE_CHAMPIONSHIP_PLAYER_URL_PATH
    : process.env.NEXT_PUBLIC_UPDATE_CHAMPIONSHIP_CADDIE_URL_PATH;

  const registrationStatusClass = getRegistrationStatusClass(
    selectedParticipant?.registrationStatus,
  );

  const showAcceptPoliciesBtn = checkIfShowAcceptPoliciesBtn(
    selectedParticipant,
    isPlayersDataSource,
  );

  const showCompleteRegistrationBtn = checkIfShowCompleteRegistrationBtn(
    selectedParticipant,
    isPlayersDataSource,
  );

  const showResendEmailAndSmsBtns =
    selectedParticipant?.registrationStatus === RegistrationStatus.Registered;

  const actionContext = useCommonUIActionsContext();

  const { formAPI, onSiteRegistrationAPI } = actionContext || {};

  const mutation = formAPI?.[UPDATE_MUTATION_KEY]?.useMutation();
  const authTokenMutation = formAPI?.[AUTH_TOKEN_KEY]?.useMutation();
  const sendSMSMutation =
    onSiteRegistrationAPI?.[SMS_MUTATION_KEY]?.useMutation();
  const sendEmailMutation =
    onSiteRegistrationAPI?.[EMAIL_MUTATION_KEY]?.useMutation();

  const updateMutation = mutation?.mutateAsync;

  const updateParticipant = async (updates: object) => {
    if (!participantId) return;
    const bearerToken = await getPPAuthToken(authTokenMutation.mutateAsync);
    const cognitoToken = await getBearerToken();
    const fullCallbackUrl = ensureValidUri(callbackUrl, getPPUrl());

    const res = await updateMutation?.({
      callBackUrl: fullCallbackUrl,
      bearerToken,
      cognitoToken,
      data: {
       [participantType]: {
          id: participantId,
          ...updates,
        },
      },
    });

    if (res?.success === true) {
      setFormKey(nanoid(5));
    }
  };

  const handleAcceptPolicies = () =>
    isPlayersDataSource
      ? updateParticipant({
          antiDopingPolicyAcceptance: true,
          antiBettingPolicyAcceptance: true,
        })
      : updateParticipant({
          antiCorruptionAndBettingPolicyAcceptance: true,
        });

  const handleCompleteRegistration = () => {
    isPlayersDataSource
      ? updateParticipant({
          registrationStatus: RegistrationStatus.Registered,
          onSiteRegistrationCompletedDate: new Date().toISOString(),
        })
      : updateParticipant({
          registrationStatus: RegistrationStatus.Registered,
        });
    handleSendEmail();
    handleSendSMS();
  };

  const handleSendSMS = async () => {
    const bearerToken = await getPPAuthToken(authTokenMutation.mutateAsync);
    const smsModel = getSmsModel(selectedParticipant, smsConfig);
    var result = await sendSMSMutation.mutateAsync({
      callBackUrl: ensureValidUri(smsConfig?.callbackUrl, getPPUrl()),
      bearerToken,
      data: {
        smsModel,
      },
    });

    if (result?.success) {
      notifications.success(DEFAULT_SUCCESS_SMS_MESSAGE);
      return;
    }
    notifications.error(DEFAULT_ERROR_SMS_MESSAGE);
  };

  const handleSendEmail = async () => {
    const bearerToken = await getPPAuthToken(authTokenMutation.mutateAsync);
    const emailModel = getEmailModel(selectedParticipant, emailConfig);
    var result = await sendEmailMutation.mutateAsync({
      bearerToken,
      callBackUrl: ensureValidUri(emailConfig?.callbackUrl, getPPUrl()),
      data: { emailModel },
    });
    if (result?.success) {
      notifications.success(DEFAULT_SUCCESS_EMAIL_MESSAGE);
      return;
    }
    notifications.error(DEFAULT_ERROR_EMAIL_MESSAGE);
  };

  return (
    <>
      <div className={styles.formHeader}>
        <div className={styles.nameAndStatus}>
          <h1 className={styles.title}>{selectedParticipant?.name}</h1>
          <span
            className={`${styles.registrationStatus} ${registrationStatusClass}`}
          >
            {
              registrationStatusMapper.registrationStatus[
                selectedParticipant?.registrationStatus
              ]
            }
          </span>
        </div>

        <div className={styles.controls}>
          {showResendEmailAndSmsBtns && (
            <>
              <Button
                variant="contained"
                className={styles.button}
                onClick={handleSendSMS}
              >
                {actionBtnLabels.reSendSMSBtnLabel}
              </Button>

              <Button
                variant="contained"
                className={styles.button}
                onClick={handleSendEmail}
              >
                {actionBtnLabels.reSendEmailBtnLabel}
              </Button>
            </>
          )}

          {showAcceptPoliciesBtn && (
            <Button
              variant="contained"
              className={styles.button}
              onClick={handleAcceptPolicies}
            >
              {actionBtnLabels.agreeToPoliciesBtnLabel}
            </Button>
          )}

          {showCompleteRegistrationBtn && (
            <Button
              variant="contained"
              className={styles.button}
              onClick={handleCompleteRegistration}
            >
              {actionBtnLabels.completeRegistrationBtnLabel}
            </Button>
          )}
        </div>
      </div>
      <div className={`${styles.policiesSection} ${styles.card}`}>
        <h2 className={styles.title}>
          {championshipPolicies.policySectionTitle}
        </h2>

        {championshipPolicies.antiDopingPolicyText && (
          <PolicyItem
            text={championshipPolicies.antiDopingPolicyText}
            accepted={selectedParticipant?.antiDopingPolicyAcceptance}
          />
        )}

        {championshipPolicies.antiBettingPolicyText && (
          <PolicyItem
            text={championshipPolicies.antiBettingPolicyText}
            accepted={
              selectedParticipant?.antiBettingPolicyAcceptance ||
              selectedParticipant?.antiCorruptionAndBettingPolicyAcceptance
            }
          />
        )}
      </div>
      <PlayerPortalsForm
        key={formKey}
        formCodename={registrationForm?.formCodename}
        formHeaderData={registrationForm?.formHeaderData}
        steps={registrationForm?.steps}
        formFooterData={registrationForm?.formFooterData}
        errorsDictionary={registrationForm?.errorsDictionary}
        successMessagesDictionary={registrationForm?.successMessagesDictionary}
        nextStepsAfterSubmit={registrationForm?.nextStepsAfterSubmit}
        backgroundImage={registrationForm?.backgroundImage}
        formType={registrationForm?.formType}
        defaultValuesJson={registrationForm?.defaultValuesJson}
        defaultValuesCallbackUrl={registrationForm?.defaultValuesCallbackUrl}
        onDefaultValuesFetched={values => {
          setSelectedParticipant(values[participantType]);
        }}
        customId={participantId}
      />
      <br />
      {championshipPolicies?.privacyPolicySectionText &&
        championshipPolicies?.privacyPolicySectionText?.value
          ?.replace(/<[^>]*>/g, '')
          .trim().length > 0 && (
          <div className={styles.card}>
            <RichText content={championshipPolicies.privacyPolicySectionText} />
          </div>
        )}
    </>
  );
};

export default RegistrationForm;
